import { createGame, GameCreateZ, postEvents } from '#shared/api/games';
import type { EventBatchRes } from '#shared/core/event-batch';
import { uuidv7 } from '#shared/core/id';
import { depthOf, maskCoverage } from '#shared/domain/scoring/index';
import type { Game, GamePlayer } from '#shared/schemas/game';
import type { Rules } from '#shared/schemas/rules';
import { fill } from '@/copy';
import { api } from '@/lib/api';
import { enqueueCreate, outbox, registerOutboxStream } from '@/lib/outbox';
import { queryClient } from '@/lib/query';
import { serverNow } from '@/lib/signals';
import { toast } from '@/lib/toast';
import { flushPendingGuests } from '../players/public';
import { copy } from './copy';
import { fetchGame, gameKeys } from './queries';
import { markActive, useGameStore } from './store';

/**
 * Where the keeper's phone meets the outbox. Two jobs:
 *
 *  - `startGame` makes the game on this phone first (so the pad works with no signal) and queues the
 *    create with `enqueueCreate`, always before the first event, even when online.
 *  - `onBatch` reads every batch answer: rejected plays leave the local fold ("These plays didn't
 *    count, someone else had the book."), the rest stay folded until the server's copy replaces them.
 */

export interface StartInput {
  mode: Game['mode'];
  rules: Rules;
  coverage: number;
  honor: boolean;
  players: GamePlayer[];
  /** Who holds the book at epoch 1. null or the creator's own id: this phone. */
  keeperMemberId: string | null;
  /** undefined: auto, null: detached. */
  sessionId?: string | null;
  tournamentId?: string | null;
  fixtureId?: string | null;
  /** An Iso Gauntlet drill session this game counts for (combine). */
  drillSessionId?: string | null;
  me: { memberId: string; deviceId: string };
}

export interface StartResult {
  id: string;
  /** The sitter who holds the book now: their copy of the game is on the server, with them as keeper. */
  handedTo: string | null;
  /** The sitter who was asked for but could not get the book (no signal). This phone keeps it. */
  deferredTo: string | null;
}

/** The sitter the creator wants to hand the book to, or null when the book stays on this phone. */
export function sitterOf(keeperMemberId: string | null | undefined, meMemberId: string): string | null {
  return keeperMemberId && keeperMemberId !== meMemberId ? keeperMemberId : null;
}

const phoneOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false;

/**
 * Make the game and queue its create. Resolves the moment it is safe on this phone.
 *
 * The book goes to a sitter only when it can reach them: the phone is online and the create has landed
 * with the sitter as keeper. Otherwise (no signal, or the create did not land) the create is queued with
 * this phone as keeper, so the pad works here, and `deferredTo` says who was meant to get it. Without
 * that, a game made on a court with no signal would sit on a read-only page that cannot score.
 */
export async function startGame(i: StartInput): Promise<StartResult> {
  const id = uuidv7();
  const now = serverNow();
  const sitter = sitterOf(i.keeperMemberId, i.me.memberId);
  const handOff = sitter !== null && phoneOnline();
  const bodyFor = (keeper: string | null) =>
    GameCreateZ.parse({
      id,
      mode: i.mode,
      depth: depthOf(maskCoverage(i.mode, i.coverage)),
      coverage: maskCoverage(i.mode, i.coverage),
      rules: i.rules,
      honor: i.honor && i.mode === '1v1',
      players: i.players,
      playedAt: now,
      clientTs: now,
      sessionId: i.sessionId,
      keeperMemberId: keeper ?? undefined,
      tournamentId: i.tournamentId ?? undefined,
      fixtureId: i.fixtureId ?? undefined,
      drillSessionId: i.drillSessionId ?? undefined,
    });
  const gameFor = (body: ReturnType<typeof bodyFor>): Game => {
    const keeperMemberId = body.keeperMemberId ?? i.me.memberId;
    return {
      id,
      mode: body.mode,
      depth: body.depth,
      coverage: body.coverage ?? i.coverage,
      rules: body.rules,
      honor: body.honor,
      status: 'live',
      scoreA: 0,
      scoreB: 0,
      winner: null,
      playedAt: now,
      endedAt: null,
      keeperMemberId,
      keeperDeviceId: keeperMemberId === i.me.memberId ? i.me.deviceId : null,
      keeperEpoch: 1,
      revision: 0,
      tournamentId: body.tournamentId ?? null,
      fixtureId: body.fixtureId ?? null,
      drillSessionId: body.drillSessionId ?? null,
      sessionId: typeof body.sessionId === 'string' ? body.sessionId : null,
      createdBy: i.me.memberId,
      createdAt: now,
      deletedAt: null,
    };
  };

  const body = bodyFor(handOff ? sitter : null);
  const game = gameFor(body);
  const store = useGameStore.getState();
  await store.load(id);
  store.setGame(id, game, body.players, false);
  if (game.keeperMemberId === i.me.memberId) await markActive(id);
  // A guest added on this phone has to reach the server before a create that names them.
  await flushPendingGuests();
  // An Iso Gauntlet game names its drill session: the server drops a drill id it has never seen, so
  // this create waits in the outbox until the drill's own create has landed.
  const after = i.drillSessionId ? ({ kind: 'drill', id: i.drillSessionId } as const) : undefined;
  await enqueueCreate({ kind: 'game', id }, { url: api.url(createGame), body, ...(after ? { after } : {}) });
  if (!sitter) return { id, handedTo: null, deferredTo: null };
  if (!handOff) return { id, handedTo: null, deferredTo: sitter };

  // Online: let the create go up now and check that it really landed with the sitter as keeper.
  await outbox.flush();
  if (await createLanded(id)) return { id, handedTo: sitter, deferredTo: null };

  // The check failed. Either the create is still queued (the signal was not there after all), or it
  // landed and only the check failed. A queued create is swapped for one that keeps the book here
  // (`enqueueCreate` would keep the old row). A create that already landed made the sitter the
  // keeper on the server, and only the keeper or the owner can move the book, so this phone must not
  // queue a second create (the server would answer the stored game) and must not score into it.
  const mine = bodyFor(null);
  const swapped = await outbox.replaceCreate(
    { kind: 'game', id },
    { url: api.url(createGame), body: mine, ...(after ? { after } : {}) },
  );
  if (swapped === 'gone') return { id, handedTo: sitter, deferredTo: null };
  useGameStore.getState().setGame(id, gameFor(mine), mine.players, false);
  await markActive(id);
  return { id, handedTo: null, deferredTo: sitter };
}

async function createLanded(id: string): Promise<boolean> {
  try {
    await fetchGame(id);
    return true;
  } catch {
    return false;
  }
}

const FINAL_LOCK_CODES = new Set(['game_locked']);

/** Reads one batch answer. Exported for tests. */
export function onBatch(streamId: string, res: EventBatchRes): void {
  const store = useGameStore.getState();
  if (res.rejected.length) {
    store.dropLocal(
      streamId,
      res.rejected.map((r) => r.id),
    );
    const locked = res.rejected.every((r) => FINAL_LOCK_CODES.has(r.code));
    toast({
      title: locked ? copy.pad.lockedLate : copy.pad.rejected,
      body: fill(copy.pad.rejectedCount, { n: res.rejected.length }),
      tone: 'warning',
      duration: 8000,
    });
    void outbox.dismiss(res.rejected.map((r) => r.id));
  }
  if (res.accepted.length || res.duplicates.length || res.rejected.length) {
    void queryClient.invalidateQueries({ queryKey: gameKeys.detail(streamId) });
  }
}

/** Plays and creates parked from an earlier session (the app closed before the toast). Shows them once. */
export async function reviewParked(gameId: string): Promise<'create_failed' | 'plays' | null> {
  const rows = await outbox.rejected({ kind: 'game', id: gameId });
  if (rows.length === 0) return null;
  const create = rows.find((r) => r.op === 'create');
  const plays = rows.filter((r) => r.op !== 'create');
  if (plays.length) {
    useGameStore.getState().dropLocal(
      gameId,
      plays.map((r) => r.id),
    );
    toast({
      title: copy.pad.rejected,
      body: fill(copy.pad.rejectedCount, { n: plays.length }),
      tone: 'warning',
      duration: 8000,
    });
    await outbox.dismiss(plays.map((r) => r.id));
  }
  if (create) {
    await outbox.dismiss([create.id]);
    return 'create_failed';
  }
  return 'plays';
}

/** Called once from the module's init(). */
export function registerGameStream(): void {
  registerOutboxStream('game', {
    url: (id) => api.url(postEvents, { params: { id } }),
    onResult: onBatch,
    onCreated: (id) => {
      void queryClient.invalidateQueries({ queryKey: gameKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: gameKeys.live });
    },
  });
}
